import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { Status } from '../../models/common/Status';
import { RootState } from '../store';
import { apiClientWithApi } from '../../utils/http';
import { ClinicSettingsItem } from '../../models/Settings/ClinicSettings';
import { WeeklyScheduleDao } from '../../models/Settings/WeeklySchedule';
import {
  SlotSettings,
  UpdateSlotSettingsRequest,
  NotificationSettings,
  UpdateNotificationSettingsRequest,
  DispensarizationSettings,
  UpdateDispensarizationSettingsRequest,
  FormSettings,
  UpdateFormSettingsRequest,
} from '../../models/Settings/NotificationSettings';

export interface UpdateClinicSettingsRequest {
  operatorPhone: string;
  operatorScheduleId: string | null;
  sipAccountId: string | null;
  callPoolId: string | null;
}

export interface ClinicSettingsDetailState {
  item: ClinicSettingsItem | null;
  status: Status;
}

const initialState: ClinicSettingsDetailState = {
  item: null,
  status: {
    responseLoading: false,
    responseError: null,
    requestSuccess: null,
    requestError: null,
  },
};

export const getClinicSettingsById = createAsyncThunk(
  'clinicSettingsDetail/getById',
  async (clinicId: string, { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.get<ClinicSettingsItem>(
        `settings/clinic/${clinicId}`,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.message);
      return rejectWithValue('Не удалось получить настройки клиники!');
    }
  },
);

export const updateClinicSettings = createAsyncThunk(
  'clinicSettingsDetail/update',
  async (
    { clinicId, data }: { clinicId: string; data: UpdateClinicSettingsRequest },
    { rejectWithValue },
  ) => {
    try {
      await apiClientWithApi.patch(`settings/clinic/${clinicId}`, data);
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.message);
      return rejectWithValue('Не удалось сохранить настройки!');
    }
  },
);

export const getSchedule = createAsyncThunk(
  'clinicSettingsDetail/getSchedule',
  async (scheduleId: string, { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.get<WeeklyScheduleDao>(
        `settings/schedule/${scheduleId}`,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.message);
      return rejectWithValue('Не удалось загрузить расписание!');
    }
  },
);

export const createSchedule = createAsyncThunk(
  'clinicSettingsDetail/createSchedule',
  async (body: WeeklyScheduleDao, { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.post<string>(
        'settings/schedule',
        body,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.message);
      return rejectWithValue('Не удалось сохранить расписание!');
    }
  },
);

export const getSlotSettings = createAsyncThunk(
  'clinicSettingsDetail/getSlotSettings',
  async (clinicId: string, { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.get<SlotSettings>(
        `settings/clinic/${clinicId}/slot`,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.message);
      return rejectWithValue('Не удалось загрузить настройки задач по слотам!');
    }
  },
);

export const updateSlotSettings = createAsyncThunk(
  'clinicSettingsDetail/updateSlotSettings',
  async (
    { clinicId, data }: { clinicId: string; data: UpdateSlotSettingsRequest },
    { rejectWithValue },
  ) => {
    try {
      await apiClientWithApi.patch(`settings/clinic/${clinicId}/slot`, data);
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.message);
      return rejectWithValue('Не удалось сохранить настройки задач по слотам!');
    }
  },
);

export const getNotificationSettings = createAsyncThunk(
  'clinicSettingsDetail/getNotificationSettings',
  async (clinicId: string, { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.get<NotificationSettings>(
        `settings/clinic/${clinicId}/notification`,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.message);
      return rejectWithValue('Не удалось загрузить настройки информирования!');
    }
  },
);

export const updateNotificationSettings = createAsyncThunk(
  'clinicSettingsDetail/updateNotificationSettings',
  async (
    {
      clinicId,
      data,
    }: { clinicId: string; data: UpdateNotificationSettingsRequest },
    { rejectWithValue },
  ) => {
    try {
      await apiClientWithApi.patch(
        `settings/clinic/${clinicId}/notification`,
        data,
      );
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.message);
      return rejectWithValue('Не удалось сохранить настройки информирования!');
    }
  },
);

/* ── Dispensarization ─────────────────────────────────── */

export const getDispensarizationSettings = createAsyncThunk(
  'clinicSettingsDetail/getDispensarizationSettings',
  async (clinicId: string, { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.get<DispensarizationSettings>(
        `settings/clinic/${clinicId}/dispensarization`,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.message);
      return rejectWithValue('Не удалось загрузить настройки диспансеризации!');
    }
  },
);

export const updateDispensarizationSettings = createAsyncThunk(
  'clinicSettingsDetail/updateDispensarizationSettings',
  async (
    {
      clinicId,
      data,
    }: { clinicId: string; data: UpdateDispensarizationSettingsRequest },
    { rejectWithValue },
  ) => {
    try {
      await apiClientWithApi.patch(
        `settings/clinic/${clinicId}/dispensarization`,
        data,
      );
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.message);
      return rejectWithValue('Не удалось сохранить настройки диспансеризации!');
    }
  },
);

/* ── Form (Анкетирование) ─────────────────────────────── */

export const getFormSettings = createAsyncThunk(
  'clinicSettingsDetail/getFormSettings',
  async (clinicId: string, { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.get<FormSettings>(
        `settings/clinic/${clinicId}/form`,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.message);
      return rejectWithValue('Не удалось загрузить настройки анкетирования!');
    }
  },
);

export const updateFormSettings = createAsyncThunk(
  'clinicSettingsDetail/updateFormSettings',
  async (
    { clinicId, data }: { clinicId: string; data: UpdateFormSettingsRequest },
    { rejectWithValue },
  ) => {
    try {
      await apiClientWithApi.patch(`settings/clinic/${clinicId}/form`, data);
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.message);
      return rejectWithValue('Не удалось сохранить настройки анкетирования!');
    }
  },
);

export const clinicSettingsDetailSlice = createSlice({
  name: 'clinicSettingsDetail',
  initialState,
  reducers: {
    clearClinicSettingsDetail(state) {
      state.item = null;
      state.status.requestSuccess = null;
      state.status.requestError = null;
    },
    updateRequestStatus(state) {
      state.status.requestSuccess = null;
      state.status.requestError = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getClinicSettingsById.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
      })
      .addCase(getClinicSettingsById.fulfilled, (state, action) => {
        state.status.responseLoading = false;
        state.item = action.payload;
      })
      .addCase(getClinicSettingsById.rejected, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = 'Не удалось загрузить настройки клиники';
      })
      .addCase(updateClinicSettings.pending, (state) => {
        state.status.requestError = null;
      })
      .addCase(updateClinicSettings.fulfilled, (state) => {
        state.status.requestSuccess = 'Настройки успешно сохранены';
      })
      .addCase(updateClinicSettings.rejected, (state, action) => {
        state.status.requestError = action.payload as string;
      })
      .addCase(updateSlotSettings.rejected, (state, action) => {
        state.status.requestError = action.payload as string;
      })
      .addCase(updateNotificationSettings.rejected, (state, action) => {
        state.status.requestError = action.payload as string;
      })
      .addCase(updateDispensarizationSettings.rejected, (state, action) => {
        state.status.requestError = action.payload as string;
      })
      .addCase(updateFormSettings.rejected, (state, action) => {
        state.status.requestError = action.payload as string;
      });
  },
});

export const { clearClinicSettingsDetail, updateRequestStatus } =
  clinicSettingsDetailSlice.actions;

export default clinicSettingsDetailSlice.reducer;

export const selectClinicSettingsDetail = (state: RootState) =>
  state.clinicSettingsDetail.item;
export const clinicSettingsDetailStatus = (state: RootState) =>
  state.clinicSettingsDetail.status;
